import { useState } from "react";
import { useSearchParams } from 'react-router-dom';
import MainForm from "./MainForm";
import FrontPage from "./FrontPage";
import GatePage from './GatePage';
import { getClass70Value, getClassLTDValue, getClassLivesValue, getClassValue, getCommissionValue, getEffectiveDateValue, isValidUrlParamFields, extractExpiryFromUrl, isUrlExpired } from "../helpers";
import { UrlParams } from "../types";
import { urlParamClassicFields, urlParamFields } from "../constants";
import ExpiredQuotePage from "./ExpiredQuotePage";

export default function HomePage() {
    const [formStep, setFormStep] = useState<boolean>(false);
    const [searchParams] = useSearchParams();

    const values = searchParams.get('values');
    let urlParams: UrlParams = {
        urlId: '',
        sponsor: '',
        effective: '',
        classa: '',
        classaltd: '',
        classalives: '',
        classb: '',
        classbltd: '',
        classblives: '',
        classa70: '',
        classa70ltd: '',
        classa70lives: '',
        classb70: '',
        classb70ltd: '',
        classb70lives: '',
        commissions: '',
        commissionsLife: '',
        commissionsDepLife: '',
        commissionsADD: '',
        commissionsLTD: '',
        commissionsEHC: '',
        commissionsDental: '',
        expiryDate: '',
        isExpired: false,
        minimumHours: 20,
    };
    let isValidFields = false;
    
    if (!!values) {
        const values = searchParams.get('values');
        const sponsor = searchParams.get('sponsor');
        // Classes A70 and B70 are appended after the first 164 chars (expiry + minimum hours suffixes).
        // Older URLs end at or before that point, so the suffixes are read from the first 164 chars only.
        const baseValues = values ? values.slice(0, 164) : '';
        let expirySuffix = baseValues.length >= 160 ? baseValues.slice(-20, -10) : null;

        if (baseValues.length >= 149 && !expirySuffix) {
            expirySuffix = baseValues.slice(-10);
        }

        // Parse minimum hours from first 2 hex chars of the last 10-char suffix (e.g. 0F=15, 14=20)
        const minHoursHex = baseValues ? baseValues.slice(-10, -8) : null;
        const minHoursDecimal = minHoursHex ? parseInt(minHoursHex, 16) : 20;
        const minimumHours = (minHoursDecimal === 15 || minHoursDecimal === 20) ? minHoursDecimal : 20;

        // Handle expiry date extraction
        const expiryInfo = expirySuffix ? extractExpiryFromUrl(expirySuffix) : null;
        const urlExpired = expirySuffix ? isUrlExpired(expirySuffix) : false;



        const urlId = values ? values.slice(0, 14) : '';
        const effectiveDate = values ? values.slice(14, 21) : '';
        const classA = values ? values.slice(24, 26) : '';
        const classALTD = values ? values.slice(34, 36) : '';
        const classALives = values ? values.slice(44, 46) : '';

        const classB = values ? values.slice(54, 56) : '';
        const classBLTD = values ? values.slice(64, 66) : '';
        const classBLives = values ? values.slice(74, 76) : '';

        // Missing on older URLs, in which case these are empty and the classes stay hidden
        const classA70 = values ? values.slice(164, 166) : '';
        const classA70LTD = values ? values.slice(174, 176) : '';
        const classA70Lives = values ? values.slice(184, 186) : '';

        const classB70 = values ? values.slice(194, 196) : '';
        const classB70LTD = values ? values.slice(204, 206) : '';
        const classB70Lives = values ? values.slice(214, 216) : '';

        const commissionsLife = values ? values.slice(84, 88) : '';
        const commissionsDepLife = values ? values.slice(94, 98) : '';
        const commissionsADD = values ? values.slice(104, 108) : '';
        const commissionsLTD = values ? values.slice(114, 118) : '';
        const commissionsEHC = values ? values.slice(124, 128) : '';
        const commissionsDental = values ? values.slice(134, 138) : '';


        urlParams = {
            sponsor,
            urlId,
            effective: getEffectiveDateValue(effectiveDate),
            classa: getClassValue(classA),
            classaltd: getClassLTDValue(classALTD),
            classalives: getClassLivesValue(classALives),
            classb: getClassValue(classB),
            classbltd: getClassLTDValue(classBLTD),
            classblives: getClassLivesValue(classBLives),
            classa70: getClass70Value(classA70),
            classa70ltd: getClassLTDValue(classA70LTD),
            classa70lives: getClassLivesValue(classA70Lives),
            classb70: getClass70Value(classB70),
            classb70ltd: getClassLTDValue(classB70LTD),
            classb70lives: getClassLivesValue(classB70Lives),
            commissionsLife: getCommissionValue(commissionsLife),
            commissionsDepLife: getCommissionValue(commissionsDepLife),
            commissionsADD: getCommissionValue(commissionsADD),
            commissionsLTD: getCommissionValue(commissionsLTD),
            commissionsEHC: getCommissionValue(commissionsEHC),
            commissionsDental: getCommissionValue(commissionsDental),
            expiryDate: expiryInfo ? expiryInfo.formattedDate : '',
            isExpired: urlExpired,
            minimumHours,
        };

        isValidFields = isValidUrlParamFields(urlParams, urlParamFields) && !urlExpired;
    } else {
        urlParams = {
            urlId: 'N/A',
            sponsor: searchParams.get('sponsor'),
            effective: searchParams.get('effective'),
            classa: searchParams.get('ClassA'),
            classaltd: searchParams.get('ClassALTD'),
            classalives: searchParams.get('ClassALives'),
            classb: searchParams.get('ClassB'),
            classbltd: searchParams.get('ClassBLTD'),
            classblives: searchParams.get('ClassBLives'),
            commissions: searchParams.get('Commissions'),
            expiryDate: '',
            isExpired: false,
            minimumHours: 20,
        };

        isValidFields = isValidUrlParamFields(urlParams, urlParamClassicFields);
    }

    if (!isValidFields) {
        // If URL is expired, show the specialized expired quote page
        if (urlParams.isExpired) {
            return (
                <ExpiredQuotePage 
                    expiryDate={urlParams.expiryDate} 
                    companyName={urlParams.sponsor}
                    quoteId={urlParams.urlId}
                />
            );
        }
        // Otherwise show the standard gate page
        return <GatePage />
    }

    return (
        <div className="home-page">
            {
                !formStep ? <FrontPage onSubmit={setFormStep} /> : <MainForm urlParams={urlParams} />
            }
        </div>
    );
};